import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { X, Key, Mail, Send, Clock, Trash2, ShieldCheck, Loader2, Link as LinkIcon, Check } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";

import { collection, deleteDoc, doc, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../config/firebase";

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {function} props.onClose
 * @param {any} props.company
 */
const CompanyAccessModal = ({ isOpen, onClose, company }) => {
  const brandColor = "#00a99d";
  
  // 🟢 BLINDAGEM DE TIPAGEM: JSDoc para eliminar os erros "never"
  const [invites, setInvites] = useState(/** @type {any[]} */ ([]));
  const [loadingInvites, setLoadingInvites] = useState(true);
  
  // Controles de estado individuais para as ações da lista
  const [sendingStates, setSendingStates] = useState(/** @type {Record<string, boolean>} */ ({}));
  const [copiedStates, setCopiedStates] = useState(/** @type {Record<string, boolean>} */ ({}));

  // ==========================================
  // BÚSSOLA DINÂMICA (Auditoria de Ambiente em Tempo Real)
  // ==========================================
  // Garante que o ambiente seja lido corretamente, mesmo se o .env falhar no ciclo de vida do React.
  const getCurrentEnvironment = () => {
    const envVar = process.env.REACT_APP_FIREBASE_DATABASE_ID;
    if (envVar) return envVar;
    
    // Fallback: Auditoria pela URL
    const host = window.location.hostname;
    if (host.includes("homolog") || host.includes("localhost")) return "homolog";
    return "(default)";
  };

  const activeEnvironment = getCurrentEnvironment();

  useEffect(() => {
    if (!isOpen || !company) return;

    const invitesRef = collection(db, "companies", company.id, "invites");
    const unsubscribe = onSnapshot(invitesRef, (/** @type {any} */ snapshot) => {
      const invitesData = snapshot.docs.map((/** @type {any} */ d) => ({
        id: d.id, 
        ...d.data()
      }));
      setInvites(invitesData);
      setLoadingInvites(false);
    }, (/** @type {Error} */ error) => {
      console.error("Erro ao buscar convites:", error);
      setLoadingInvites(false);
    });

    return () => unsubscribe();
  }, [isOpen, company]);

  // ==========================================
  // DISPARO NOVO CONVITE (Chama a Cloud Function Oficial)
  // ==========================================
  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: Yup.object({
      email: Yup.string().email("E-mail inválido").required("Obrigatório"),
    }),
    onSubmit: async (/** @type {any} */ values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      try {
        const inviteFn = httpsCallable(functions, 'inviteUserWithLimitCheck');
        
        await inviteFn({
          companyId: company.id,
          companyName: company.nome || company.companyName || "Imobiliária",
          newUserData: {
            email: values.email.toLowerCase(),
            name: "Administrador(a)", 
            role: "admin",
            status: "pending"
          },
          // 🧭 BÚSSOLA INJETADA: Avisando o Backend qual banco usar
          environment: activeEnvironment,
          clientBaseUrl: window.location.origin
        });

        alert("Convite gerado e e-mail enviado com sucesso!");
        resetForm();
      } catch (error) {
        const catchedError = /** @type {Error} */ (error);
        console.error("Erro ao processar convite:", catchedError);
        alert(catchedError.message || "Erro ao processar o convite. Tente novamente.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // ==========================================
  // RE-DISPARO DE E-MAIL (Chama a função manual)
  // ==========================================
  const handleResendInvite = async (/** @type {string} */ inviteEmail) => {
    setSendingStates(prev => ({ ...prev, [inviteEmail]: true }));
    try {
      const sendInviteEmailFn = httpsCallable(functions, 'sendInviteEmail');
      await sendInviteEmailFn({ 
        email: inviteEmail, 
        name: "Administrador(a)",
        // 🧭 BÚSSOLA INJETADA: Avisando o Backend qual banco usar
        environment: activeEnvironment,
        clientBaseUrl: window.location.origin
      });
      alert(`E-mail reenviado com sucesso para ${inviteEmail}!`);
    } catch (error) {
      console.error("Erro ao reenviar convite:", error);
      alert("Erro ao disparar o e-mail de reenvio. Tente novamente mais tarde.");
    } finally {
      setSendingStates(prev => ({ ...prev, [inviteEmail]: false }));
    }
  };

  // ==========================================
  // CÓPIA MANUAL DO LINK (Rota de Fuga / Fallback)
  // ==========================================
  const handleCopyLink = (/** @type {string} */ inviteEmail) => {
    const inviteLink = `${window.location.origin}/invite?email=${encodeURIComponent(inviteEmail)}`;
    navigator.clipboard.writeText(inviteLink);
    
    setCopiedStates(prev => ({ ...prev, [inviteEmail]: true }));
    setTimeout(() => {
      setCopiedStates(prev => ({ ...prev, [inviteEmail]: false }));
    }, 2000);
  };

  // ==========================================
  // CANCELAR CONVITE PENDENTE
  // ==========================================
  const handleDeleteInvite = async (/** @type {string} */ emailId) => {
    if (window.confirm(`Tem certeza que deseja cancelar o convite pendente para ${emailId}?`)) {
      try {
        await deleteDoc(doc(db, "companies", company.id, "invites", emailId));
      } catch (error) {
        console.error("Erro ao excluir convite:", error);
        alert("Erro ao cancelar o convite.");
      }
    }
  };

  if (!isOpen || !company) return null;

  const displayName = company.nome || company.companyName || "Empresa Parceira";

  return ReactDOM.createPortal(
    <div 
      className="flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4 sm:p-6"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh', zIndex: 999999 }}
    >
      <div className="relative w-full max-w-2xl flex flex-col bg-white dark:bg-dark-card rounded-2xl shadow-2xl border border-light-border dark:border-dark-border overflow-hidden" style={{ maxHeight: '90vh' }}>
        
        {/* HEADER (Com Flexbox Avançado para Truncar Títulos Gigantes) */}
        <div className="flex items-start justify-between p-5 lg:p-6 border-b border-light-border dark:border-dark-border bg-gray-50/50 dark:bg-dark-bg/50 shrink-0 gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="p-2 rounded-lg text-white shadow-sm shrink-0" style={{ backgroundColor: brandColor }}>
              <Key className="w-5 h-5 lg:w-6 lg:h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg lg:text-xl font-bold text-gray-900 dark:text-white truncate" title={`Acessos: ${displayName}`}>
                Acessos: {displayName}
              </h3>
              <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                Convide o administrador responsável por esta imobiliária.
              </p>
            </div>
          </div>
          <button
            onClick={() => { formik.resetForm(); onClose(); }}
            className="text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-dark-border dark:hover:text-white rounded-full p-2 transition-colors shrink-0"
          >
            <X className="w-5 h-5 lg:w-6 lg:h-6" />
          </button>
        </div>

        {/* CORPO */}
        <div className="p-5 lg:p-6 space-y-6 lg:space-y-8 overflow-y-auto custom-scrollbar flex-1 min-h-0">
          
          <form onSubmit={formik.handleSubmit} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Mail className="w-4 h-4 lg:w-5 lg:h-5 text-gray-400" />
              </div>
              <input
                type="email"
                {...formik.getFieldProps("email")}
                className={`form-input w-full pl-9 lg:pl-10 rounded-lg text-sm p-2.5 lg:p-3 dark:bg-dark-bg dark:text-white outline-none ${
                  formik.touched.email && formik.errors.email ? 'border-red-500 ring-1 ring-red-500' : 'border border-gray-300 dark:border-dark-border focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500'
                }`}
                placeholder="E-mail do administrador..."
              />
            </div>
            <button
              type="submit"
              disabled={formik.isSubmitting || !formik.values.email}
              className="inline-flex items-center justify-center px-6 py-2.5 lg:py-3 text-xs lg:text-sm font-bold text-white rounded-lg shadow-md focus:ring-4 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 shrink-0"
              style={{ backgroundColor: brandColor }}
            >
              {formik.isSubmitting ? <Loader2 className="w-4 h-4 lg:w-5 lg:h-5 animate-spin" /> : (
                <>
                  <Send className="w-3.5 h-3.5 lg:w-4 lg:h-4 mr-2" />
                  Gerar Novo Convite
                </>
              )}
            </button>
          </form>

          <div>
            <h4 className="text-xs lg:text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 border-b border-gray-100 dark:border-dark-border pb-2">
              Convites Pendentes
            </h4>
            
            {loadingInvites ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : invites.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 dark:bg-dark-bg/50 rounded-xl border border-dashed border-gray-200 dark:border-dark-border">
                <ShieldCheck className="w-6 h-6 lg:w-8 lg:h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">Nenhum convite pendente para esta imobiliária.</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-dark-border border border-gray-100 dark:border-dark-border rounded-xl overflow-hidden">
                {invites.map((/** @type {any} */ invite) => (
                  <li key={invite.id} className="p-3 lg:p-4 flex flex-col sm:flex-row sm:items-center justify-between bg-white dark:bg-dark-card hover:bg-gray-50 dark:hover:bg-dark-bg/50 transition-colors gap-3">
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 shrink-0">
                        <Clock className="w-4 h-4 lg:w-5 lg:h-5" />
                      </div>
                      <div className="min-w-0 pr-2">
                        <p className="text-xs lg:text-sm font-bold text-gray-900 dark:text-white truncate" title={invite.email}>{invite.email}</p>
                        <p className="text-[10px] lg:text-xs text-gray-500 dark:text-gray-400 mt-0.5">Enviado em: {invite.createdAt?.toDate().toLocaleDateString() || 'Agora'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-auto border-t sm:border-t-0 border-gray-100 dark:border-dark-border w-full sm:w-auto pt-3 sm:pt-0 justify-end shrink-0">
                      
                      {/* NOVO BOTÃO: COPIAR LINK */}
                      <button 
                        onClick={() => handleCopyLink(invite.email)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors border ${
                          copiedStates[invite.email] 
                            ? "bg-green-50 text-green-600 border-green-200 dark:bg-green-900/20 dark:border-green-800" 
                            : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 dark:bg-transparent dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-800"
                        }`}
                        title="Copiar link de convite manualmente"
                      >
                        {copiedStates[invite.email] ? <Check className="w-3.5 h-3.5" /> : <LinkIcon className="w-3.5 h-3.5" />}
                        <span className="hidden sm:inline">{copiedStates[invite.email] ? "Copiado!" : "Copiar Link"}</span>
                      </button>

                      <button 
                        onClick={() => handleResendInvite(invite.email)}
                        disabled={sendingStates[invite.email]}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-teal-600 bg-teal-50 hover:bg-teal-100 dark:bg-teal-900/20 dark:hover:bg-teal-900/40 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-teal-100 dark:border-teal-800"
                        title="Reenviar e-mail de convite"
                      >
                        {sendingStates[invite.email] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        <span className="hidden sm:inline">Reenviar</span>
                      </button>

                      <button 
                        onClick={() => handleDeleteInvite(invite.id)}
                        className="flex items-center justify-center p-1.5 lg:p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors shrink-0"
                        title="Cancelar Convite"
                      >
                        <Trash2 className="w-4 h-4 lg:w-5 lg:h-5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CompanyAccessModal;